import { HttpClient } from '@angular/common/http';
import { Injectable, OnDestroy } from '@angular/core';

import { ReplaySubject } from 'rxjs';

import { SystemService } from 'app/services/system.service';
import { StorageAPIService } from '../storage-api.service';

export interface ObjectLabel {
  id: string;
  label: string;
  groupId: string;
  i18n?: { [key: string]: string };
  createdAt: string;
  modifiedAt: string;
}

export interface ItemLabel extends ObjectLabel {
  tooltip: string;
  type: 'system' | 'user';
}

export interface ItemLabelWrapper {
  type: ObjectLabelObjectType | string;
  subType?: string;
  id: string;
  labels: Partial<ItemLabel>[];
  searchLabels?: ObjectLabel[];
}

export type Sideloadable<T> = T & { sideloaded?: { objectLabels: ObjectLabel[] } };

export type ObjectLabelObjectType =
  | 'dashboard'
  | 'collection'
  | 'analysis'
  | 'analysisGroup'
  | 'driverAnalysis'
  | 'insight'
  | 'feed';

@Injectable({
  providedIn: 'root',
})
export class ObjectLabelService extends StorageAPIService implements OnDestroy {
  private lastGroupId: string;
  private objectLabelsCache = new ReplaySubject(1);
  objectLabels$ = this.objectLabelsCache.asObservable();

  constructor(httpClient: HttpClient, systemService: SystemService) {
    super(httpClient, systemService);
  }

  ngOnDestroy(): void {
    this.objectLabelsCache.complete();
  }

  async getAllLabels(groupId: string, force?) {
    if (this.lastGroupId === groupId && !force) {
      return;
    }

    const labels = await this.getLabels(groupId, 0, StorageAPIService.MAX_LIMIT);
    const labelsById = new Map(labels.items?.map((label) => [label.id, label]));
    this.objectLabelsCache.next(labelsById);
    return labels;
  }

  getLabels(
    groupId: string,
    page: number = 0,
    pageSize: number = 20,
    query: string = '*',
    prefix: boolean = false,
    sortField: 'label' | 'createdAt' | 'modifiedAt' = 'label',
    sortOrder: 'ASC' | 'DESC' = 'DESC'
  ) {
    return this.getPage<ObjectLabel>(`/groups/${groupId}/labels`, {
      query,
      prefix,
      'sort-field': sortField,
      'sort-order': sortOrder,
      page,
      pageSize,
    });
  }

  createLabel(groupId: string, label: string) {
    return this.post<ObjectLabel>(`/groups/${groupId}/labels`, { label });
  }

  updateLabel(groupId: string, labelId: string, label: string) {
    return this.patch<ObjectLabel>(`/groups/${groupId}/labels/${labelId}`, { label });
  }

  applyLabel(
    groupId: string,
    labelId: string,
    objectType: ObjectLabelObjectType,
    objectId: string
  ) {
    return this.post(`/groups/${groupId}/labels/${labelId}/_apply`, [{ objectType, objectId }]);
  }

  setLabels(
    groupId: string,
    labelIdList: string[],
    objectType: ObjectLabelObjectType,
    objectId: string
  ) {
    return this.post(`/groups/${groupId}/labels/set`, { objectType, objectId, labelIdList });
  }

  removeLabel(
    groupId: string,
    labelId: string,
    objectType: ObjectLabelObjectType,
    objectId: string
  ) {
    return this.post(`/groups/${groupId}/labels/${labelId}/_remove`, [{ objectType, objectId }]);
  }

  removeLabelFromAllObjects(groupId: string, labelId: string) {
    return this.post(`/groups/${groupId}/labels/${labelId}/_remove_all`);
  }
}
